import { useMemo, useState } from 'react'

type SortDir = 'asc' | 'desc'

export function useSort<T>(
  items: T[],
  defaultKey: keyof T,
  defaultDir: SortDir = 'asc',
) {
  const [sortKey, setSortKey] = useState<keyof T>(defaultKey)
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir)

  function handleSort(key: keyof T) {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      // Null/empty values sort last regardless of direction
      if (av == null || av === '') return 1
      if (bv == null || bv === '') return -1
      let cmp: number
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv
      } else {
        cmp = String(av).localeCompare(String(bv))
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [items, sortKey, sortDir])

  return { sorted, sortKey, sortDir, handleSort }
}
